import { useMemo } from 'react';

import { ActiveProductDetailsResponse, ActiveProductsCardResponse } from '@eneco/dc-be/client';
import { Badge, Box, Heading, Stack, Text, TextLink } from '@eneco/sparky';
import { styled } from '@eneco/sparky/stitches';
import { useContent } from '@sitecore/common';
import { DashboardOverviewExtended } from '@sitecore/types/manual/be/DashboardOverviewExtended';

import { StyledDashboardBlock } from './DashboardBlock';
import { getSitecoreProduct } from '../../../../utils/getSitecoreProduct';
import { EnergyIcon } from '../../advance/components/EnergyIcon';
import { PRODUCT_STATUS_COLOR_MAP } from '../../products/utils/statusColors';

export const ActiveProductsCard = ({ card }: { card: ActiveProductsCardResponse }) => {
  const { activeProducts } = card;
  const { fields } = useContent<DashboardOverviewExtended>();

  return (
    <StyledDashboardBlock>
      <Stack gap={2}>
        {activeProducts.map((product, index) => (
          <ProductCard key={`${product.productName}_${product.energyType}_${index}`} activeProduct={product} />
        ))}
        <TextLink href={fields.activeProductsCard?.link?.value?.href} emphasis="high">
          {fields.activeProductsCard?.link?.value?.text}
        </TextLink>
      </Stack>
    </StyledDashboardBlock>
  );
};

const StyledBadge = styled(Badge, {
  whiteSpace: 'unset !important',
  textAlign: 'center',
  flexBasis: 0,
});

const ProductCard = ({ activeProduct }: { activeProduct: ActiveProductDetailsResponse }) => {
  const { fields } = useContent<DashboardOverviewExtended>();

  const mappedSitecoreProduct = useMemo(
    () =>
      getSitecoreProduct({
        enecoProductsList: fields?.activeProductsCard?.enecoProductsList,
        technicalProductName: activeProduct?.productName,
      }),
    [fields?.activeProductsCard?.enecoProductsList, activeProduct?.productName],
  );

  return (
    <Box padding={4} backgroundColor={'backgroundSecondary'} borderRadius={'s'}>
      <Stack direction="row" alignY="center" alignX="justify" gap={4}>
        <Stack direction="row" alignY="center" gap={4}>
          <Stack.Item shrink={false}>
            <Box paddingY={2} paddingX={5} backgroundColor={'backgroundPrimary'} borderRadius={'s'}>
              <EnergyIcon energyType={activeProduct.energyType} size="medium" />
            </Box>
          </Stack.Item>
          <Stack>
            <Text weight="bold" size={'BodyXS'}>
              {activeProduct.energyType === 'Electricity' && fields.activeProductsCard?.energyTypeElectricityText.value}
              {activeProduct.energyType === 'Gas' && fields.activeProductsCard?.energyTypeGasText.value}
            </Text>
            <Heading size="3XS" as="h3">
              {mappedSitecoreProduct?.productDisplayName?.value || activeProduct?.productName || ''}
            </Heading>
          </Stack>
        </Stack>
        <StyledBadge
          color={PRODUCT_STATUS_COLOR_MAP.get(
            activeProduct.status,
          )}>{`${fields.activeProductsCard?.productStatusesList?.value.enum.find(x => x.value?.toLocaleLowerCase() === activeProduct.status?.toLocaleLowerCase())?.label}`}</StyledBadge>
      </Stack>
    </Box>
  );
};
